import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { ClipLoader } from 'react-spinners';
import {
  AddressResponse,
  CreateAddressRequest,
  CreateAddressResponse,
  GetAddressesResponse,
} from '../../../types/apiTypes';
import {
  AddressComponents,
  cleanAddressUnit,
  getStreetLine,
  isCompleteAddress,
} from '../../../utils/AddressUtils';
import {
  MailingAddressSource,
  trackMailingAddressChosen,
} from '../../../utils/ga4';
import useApi from '../../../utils/AxiosWrapper';
import { BusinessInfoForm } from './BusinessInfo';
import { PersonalFormData } from './AccountInfo';
import FlexibleAddressInput from './FlexibleAddressInput';

/**
 * Where the cards are mailed, asked at the top of Review (THT-819).
 *
 * Nothing is stored as a "confirmation": the primary address is the answer. The options are the
 * customer's saved addresses, none is preselected, and Review's Continue applies the pick through
 * the addresses endpoints that already ship.
 */
const MAILING_ADDRESS_SOURCE = {
  Business: 'business',
  Home: 'home',
  Saved: 'saved',
  Other: 'other',
} as const satisfies Record<string, MailingAddressSource>;

/**
 * The longest unit line the addresses endpoint accepts (it 400s on more, and every retry would
 * repeat it). Mirrors `MAX_BLUE_PENGUIN_SHIP_ADDRESS2_LENGTH` in blue-penguin-utils, which the UI
 * can't import.
 */
export const MAX_UNIT_LENGTH = 40;

/** The radio value for "A different address"; every saved address uses its own address_id. */
const OTHER_OPTION = 'other';

export const MAILING_ADDRESS_TITLE =
  "If you're approved, where should we mail your TACH cards?";
export const MAILING_ADDRESS_HELPER =
  "We only mail cards after your application is approved. Choosing an address here doesn't order anything and there's no charge.";
export const MAILING_ADDRESS_CHOOSE = 'Please make a selection';
export const MAILING_ADDRESS_ERROR =
  "We couldn't save this address. Please try again.";
export const MAILING_ADDRESS_LIST_ERROR =
  "We couldn't load your saved addresses.";
export const MAILING_ADDRESS_UNIT_TOO_LONG = `Address line 2 must be at most ${MAX_UNIT_LENGTH} characters.`;

const ADDRESS_FIELDS = [
  'address_st_number',
  'address_st_name',
  'address_unit',
  'address_city',
  'address_state',
  'address_zip',
] as const;

type AddressForm = AddressComponents & { address?: string };
type CallProtectedApi = ReturnType<typeof useApi>['callProtectedApi'];

/**
 * Trim + case-insensitive over all six fields, unit included. A missing unit and the "-"
 * placeholder both count as no unit, so a saved "-" row still matches the same address retyped.
 */
export const normalizeAddress = (
  address: Partial<Record<(typeof ADDRESS_FIELDS)[number], string | null>>
): string =>
  ADDRESS_FIELDS.map((field) =>
    (field === 'address_unit'
      ? cleanAddressUnit(address?.address_unit)
      : (address?.[field] ?? '').toString().trim()
    ).toLowerCase()
  ).join('|');

const pickAddressParts = (
  source: Partial<Record<(typeof ADDRESS_FIELDS)[number], string | null>>
): AddressComponents => ({
  address_st_number: source?.address_st_number ?? '',
  address_st_name: source?.address_st_name ?? '',
  address_unit: cleanAddressUnit(source?.address_unit),
  address_city: source?.address_city ?? '',
  address_state: source?.address_state ?? '',
  address_zip: source?.address_zip ?? '',
});

const isSuccess = (response: { status: number } | null | undefined) =>
  !!response && response.status >= 200 && response.status < 300;

/**
 * Makes `address` the customer's primary address.
 *
 * Already in the list and primary → nothing to do. In the list → `setPrimary` only, so the
 * address limit's 400 on `POST /addresses` can't fire for an address that exists. Not in the
 * list → create it, then `setPrimary` the returned row. `onCreated` lets the caller remember the
 * new row so a retry after a failed `setPrimary` reuses it instead of creating a duplicate.
 */
export const applyMailingAddress = async ({
  callProtectedApi,
  customerId,
  address,
  addresses,
  onCreated,
}: {
  callProtectedApi: CallProtectedApi;
  customerId: string;
  address: AddressComponents;
  addresses: AddressResponse[];
  onCreated?: (created: AddressResponse) => void;
}): Promise<boolean> => {
  const wanted = normalizeAddress(address);
  const existing = addresses.find((row) => normalizeAddress(row) === wanted);
  if (existing?.is_primary) return true;

  let addressId = existing?.address_id;
  if (!addressId) {
    const body: CreateAddressRequest = {
      customer_id: customerId,
      address_st_number: address.address_st_number ?? '',
      address_st_name: address.address_st_name ?? '',
      // The unit is nullable on the backend; omit it when empty.
      address_unit: cleanAddressUnit(address.address_unit) || undefined,
      address_city: address.address_city ?? '',
      address_state: address.address_state ?? '',
      address_zip: address.address_zip ?? '',
    };
    const created = await callProtectedApi<{ data: CreateAddressResponse }>(
      '/addresses',
      'post',
      body
    );
    const createdAddress = created?.data?.data?.address;
    if (!isSuccess(created) || !createdAddress?.address_id) return false;
    onCreated?.(createdAddress);
    addressId = createdAddress.address_id;
  }

  const response = await callProtectedApi(
    `/addresses/${addressId}/setPrimary`,
    'post',
    {}
  );
  return isSuccess(response);
};

const cityLine = (address: AddressComponents): string =>
  [
    [address.address_city, address.address_state].filter(Boolean).join(', '),
    address.address_zip,
  ]
    .filter(Boolean)
    .join(' ');

/** What Review holds on to so Continue can apply the pick. */
export interface MailingAddressHandle {
  /** Saves the chosen address as primary; resolves true when it is saved (or already was). */
  apply: () => Promise<boolean>;
}

interface MailingAddressCardProps {
  customerId: string;
  /** Only used to tell analytics whether the pick is the business or home address. */
  businessInfo?: BusinessInfoForm;
  personalInfo?: PersonalFormData;
  /** False while a typed address can't be sent yet, so Review can keep Continue disabled. */
  onReadyChange: (ready: boolean) => void;
}

const MailingAddressCard = forwardRef<
  MailingAddressHandle,
  MailingAddressCardProps
>(function MailingAddressCard(
  { customerId, businessInfo, personalInfo, onReadyChange },
  ref
) {
  const { callProtectedApi } = useApi();
  const [loading, setLoading] = useState(true);
  const [listFailed, setListFailed] = useState(false);
  const [addresses, setAddresses] = useState<AddressResponse[]>([]);
  // Nothing is preselected: the customer has to acknowledge where cards go (THT-819 review).
  const [choice, setChoice] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [needsChoice, setNeedsChoice] = useState(false);
  const [addressSelectedFromDropdown, setAddressSelectedFromDropdown] =
    useState(false);
  const { register, control, watch, setValue, getValues, formState } =
    useForm<AddressForm>({ defaultValues: {} });
  const typed = watch();
  const cardRef = useRef<HTMLDivElement>(null);

  const addressesRef = useRef<AddressResponse[]>([]);
  const listFailedRef = useRef(false);
  const loadRef = useRef<Promise<void> | null>(null);
  // "A different address" opened by the card itself (no saved address / list failed), as opposed
  // to picked by the customer. Only the former is taken back when the real list arrives.
  const autoOpenedRef = useRef(false);

  // The options come from the addresses endpoint only — never from the customer or driver record.
  const loadAddresses = useCallback(async (): Promise<void> => {
    // callProtectedApi throws when the session isn't ready; that is a failed load like any other.
    const response = await callProtectedApi<{ data: GetAddressesResponse }>(
      `/addresses/list/${customerId}`,
      'get'
    ).catch(() => null);
    const rows = response?.data?.data?.addresses;
    if (isSuccess(response) && Array.isArray(rows)) {
      // The endpoint returns active rows newest first; the primary goes on top.
      const sorted = [...rows].sort(
        (a, b) => Number(b.is_primary) - Number(a.is_primary)
      );
      addressesRef.current = sorted;
      listFailedRef.current = false;
      setAddresses(sorted);
      setListFailed(false);
      if (sorted.length > 0 && autoOpenedRef.current) {
        autoOpenedRef.current = false;
        setChoice(null);
      }
    } else {
      listFailedRef.current = true;
      setListFailed(true);
    }
    if (!Array.isArray(rows) || rows.length === 0) {
      // Nothing to pick from: open the typed address rather than ask to click it.
      setChoice((current) => {
        if (current !== null) return current;
        autoOpenedRef.current = true;
        return OTHER_OPTION;
      });
    }
    setLoading(false);
  }, [callProtectedApi, customerId]);

  // Review passes '' until the user id resolves; asking for `/addresses/list/` then would "fail"
  // and force the typed address open for good. Wait for a real id, and start over if it changes.
  useEffect(() => {
    if (!customerId) return;
    setLoading(true);
    if (autoOpenedRef.current) {
      autoOpenedRef.current = false;
      setChoice(null);
    }
    loadRef.current = loadAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const retryLoad = () => {
    setLoading(true);
    loadRef.current = loadAddresses();
  };

  // Picking a Places suggestion fills the street, city and ZIP fields, but the search box stays
  // editable. Typing in it afterwards must not leave the earlier suggestion's fields behind —
  // the box would show one address while another is saved. Remember the text the suggestion set,
  // and clear the fields as soon as the box says something else.
  const suggestionTextRef = useRef<string | null>(null);
  const searchText = typed.address ?? '';
  useEffect(() => {
    if (
      suggestionTextRef.current !== null &&
      searchText !== suggestionTextRef.current
    ) {
      suggestionTextRef.current = null;
      ADDRESS_FIELDS.forEach((field) => setValue(field, ''));
    }
  }, [searchText, setValue]);

  const savedChoice = addresses.find((row) => row.address_id === choice);
  const chosenAddress: AddressComponents | null = savedChoice
    ? pickAddressParts(savedChoice)
    : choice === OTHER_OPTION
      ? pickAddressParts(typed)
      : null;
  const unitTooLong =
    choice === OTHER_OPTION &&
    (chosenAddress?.address_unit?.length ?? 0) > MAX_UNIT_LENGTH;
  const ready =
    choice !== OTHER_OPTION ||
    (isCompleteAddress(chosenAddress) && !unitTooLong);

  useEffect(() => {
    onReadyChange(ready);
  }, [ready, onReadyChange]);

  const sourceOf = (address: AddressComponents): MailingAddressSource => {
    if (choice === OTHER_OPTION) return MAILING_ADDRESS_SOURCE.Other;
    const key = normalizeAddress(address);
    if (key === normalizeAddress(pickAddressParts(businessInfo ?? {})))
      return MAILING_ADDRESS_SOURCE.Business;
    if (key === normalizeAddress(pickAddressParts(personalInfo ?? {})))
      return MAILING_ADDRESS_SOURCE.Home;
    return MAILING_ADDRESS_SOURCE.Saved;
  };

  const apply = async (): Promise<boolean> => {
    await loadRef.current;
    setError(false);
    if (!chosenAddress) {
      setNeedsChoice(true);
      cardRef.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'center',
      });
      return false;
    }
    // An earlier failed load would make an existing address look new, and creating it can 400 at
    // the address limit. Ask again before writing.
    if (listFailedRef.current) await loadAddresses();
    const ok = await applyMailingAddress({
      callProtectedApi,
      customerId,
      address: chosenAddress,
      addresses: addressesRef.current,
      onCreated: (created) => {
        addressesRef.current = [...addressesRef.current, created];
      },
    }).catch(() => false);
    if (!ok) {
      setError(true);
      return false;
    }
    trackMailingAddressChosen({ source: sourceOf(chosenAddress) });
    return true;
  };

  // Re-created every render on purpose, so `apply` always sees the latest choice and list.
  useImperativeHandle(ref, () => ({ apply }));

  const option = (value: string, label: string, detail?: string) => (
    <label
      key={value}
      className="flex min-w-0 max-w-full cursor-pointer flex-row items-start gap-2.5 py-1"
    >
      <input
        type="radio"
        name="mailing-address"
        value={value}
        checked={choice === value}
        // Clicking an option that is already checked fires no change event; it is still a pick.
        onClick={() => {
          autoOpenedRef.current = false;
        }}
        onChange={() => {
          autoOpenedRef.current = false;
          setChoice(value);
          setError(false);
          setNeedsChoice(false);
        }}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-full border border-gray-300 bg-white checked:bg-[radial-gradient(circle,#F7A826_0%,#F7A826_38%,transparent_42%)] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
      />
      <span className="flex min-w-0 flex-col">
        <span className="break-words text-sm font-semibold leading-tight">
          {label}
        </span>
        {detail ? (
          <span className="break-words text-xs leading-tight text-gray-600">
            {detail}
          </span>
        ) : null}
      </span>
    </label>
  );

  return (
    <div
      ref={cardRef}
      data-testid="mailing-address-card"
      className={
        needsChoice
          ? 'rounded-[4px] border-[1px] border-danger p-3 ring-1 ring-danger'
          : 'rounded-[4px] border-[1px] border-bordergray p-3'
      }
    >
      <h2 className="text-base font-semibold">{MAILING_ADDRESS_TITLE}</h2>
      <p className="mb-3 mt-0.5 text-sm text-gray-600">
        {MAILING_ADDRESS_HELPER}
      </p>
      {loading ? (
        <div className="mb-3 flex justify-start">
          <ClipLoader
            color="#1D213B"
            size={20}
            data-testid="mailing-address-loading"
          />
        </div>
      ) : null}
      {listFailed && !loading ? (
        <p className="mb-2 text-sm text-gray-600">
          {MAILING_ADDRESS_LIST_ERROR}{' '}
          <button
            type="button"
            onClick={retryLoad}
            className="font-semibold text-accent underline"
          >
            Try again
          </button>
        </p>
      ) : null}
      <div
        className="flex flex-wrap gap-x-10 gap-y-1"
        role="radiogroup"
        aria-invalid={needsChoice}
      >
        {addresses.map((row) =>
          option(
            row.address_id,
            getStreetLine(pickAddressParts(row)),
            cityLine(pickAddressParts(row))
          )
        )}
        {!loading ? option(OTHER_OPTION, 'A different address') : null}
      </div>
      {choice === OTHER_OPTION ? (
        <div className="relative mt-3 w-full">
          <FlexibleAddressInput
            label="Address"
            searchFieldName="address"
            streetNumberFieldName="address_st_number"
            streetNameFieldName="address_st_name"
            unitFieldName="address_unit"
            cityFieldName="address_city"
            stateFieldName="address_state"
            zipFieldName="address_zip"
            register={register}
            control={control}
            watch={watch}
            setValue={setValue}
            errors={formState.errors}
            initialMode="autocomplete"
            requireCompleteAddress
            searchPlaceholder="Enter Address"
            addressSelectedFromDropdown={addressSelectedFromDropdown}
            setAddressSelectedFromDropdown={setAddressSelectedFromDropdown}
            cookieKey="mailingAddressMode"
            onAddressComponentsChange={() => {
              suggestionTextRef.current = getValues('address') ?? '';
            }}
          />
          {unitTooLong ? (
            <p role="alert" className="mt-2 text-sm text-danger">
              {MAILING_ADDRESS_UNIT_TOO_LONG}
            </p>
          ) : null}
        </div>
      ) : null}
      {needsChoice ? (
        <p role="alert" className="mt-3 text-sm text-danger">
          {MAILING_ADDRESS_CHOOSE}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="mt-3 text-danger">
          {MAILING_ADDRESS_ERROR}
        </p>
      ) : null}
    </div>
  );
});

export default MailingAddressCard;
